import axios from 'axios'
import { apiClient } from '@/services/api'
import {
  CreateFeedbackRequestDto,
  SubmitFeedbackDto,
  QueryFeedbackDto,
  FeedbackRequest,
  FeedbackStats,
  FeedbackOperationResponse,
} from '@/types/feedback.types'

function toQuery(params?: QueryFeedbackDto): Record<string, string> | undefined {
  if (!params) return undefined
  const query: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') query[key] = String(value)
  }
  return Object.keys(query).length ? query : undefined
}

/**
 * Feedback API — goes through apiClient so Auth0 audience tokens and
 * 401 retry apply (same path as labeling Save).
 */
export function useFeedbackApi() {
  const createFeedbackRequest = async (
    data: CreateFeedbackRequestDto
  ): Promise<FeedbackOperationResponse> => {
    return apiClient.post('/feedback', data)
  }

  const getFeedbackRequests = async (
    params?: QueryFeedbackDto
  ): Promise<FeedbackRequest[]> => {
    return apiClient.get('/feedback', toQuery(params))
  }

  const getPendingRequests = async (): Promise<FeedbackRequest[]> => {
    return apiClient.get('/feedback/pending')
  }

  const getMyRequests = async (
    params?: QueryFeedbackDto
  ): Promise<FeedbackRequest[]> => {
    return apiClient.get('/feedback/my-requests', toQuery(params))
  }

  const getFeedbackStats = async (): Promise<FeedbackStats> => {
    return apiClient.get('/feedback/stats')
  }

  const getFeedbackRequestById = async (id: string): Promise<FeedbackRequest> => {
    return apiClient.get(`/feedback/${id}`)
  }

  const getFeedbackRequestByOffenseId = async (offenseId: string): Promise<FeedbackRequest | null> => {
    try {
      return await apiClient.get(`/feedback/by-offense/${offenseId}`)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null
      throw error
    }
  }

  const claimFeedbackRequest = async (
    id: string
  ): Promise<FeedbackOperationResponse> => {
    return apiClient.put(`/feedback/${id}/claim`)
  }

  const submitFeedback = async (
    id: string,
    data: SubmitFeedbackDto
  ): Promise<FeedbackOperationResponse> => {
    return apiClient.put(`/feedback/${id}/submit`, data)
  }

  const cancelFeedbackRequest = async (
    id: string
  ): Promise<FeedbackOperationResponse> => {
    return apiClient.delete(`/feedback/${id}`)
  }

  return {
    createFeedbackRequest,
    getFeedbackRequests,
    getPendingRequests,
    getMyRequests,
    getFeedbackStats,
    getFeedbackRequestById,
    getFeedbackRequestByOffenseId,
    claimFeedbackRequest,
    submitFeedback,
    cancelFeedbackRequest,
  }
}
